import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, ChevronRight, ChevronLeft, Crown, Users, BookOpen,
  Music, Heart, Scroll, Megaphone, User, Building2, Check,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { logger } from '../../lib/logger';

// ── Types ──────────────────────────────────────────────────────────────────────

type MinistryRole =
  | 'Senior / Lead Pastor'
  | 'Associate Pastor'
  | 'Youth Pastor'
  | "Children's Ministry Leader"
  | 'Bible Study Leader'
  | 'Worship Leader'
  | 'Chaplain'
  | 'Seminary Student'
  | 'Lay Preacher / Evangelist'
  | 'Personal Study';

type Denomination =
  | 'Baptist'
  | 'Reformed / Presbyterian'
  | 'Methodist / Wesleyan'
  | 'Lutheran'
  | 'Anglican / Episcopal'
  | 'Pentecostal / Charismatic'
  | 'Non-denominational'
  | 'Catholic'
  | 'Other';

type CongregationSize = 'Under 50' | '50–150' | '150–500' | '500–1,000' | '1,000+';

type UseCase =
  | 'sunday_sermons'
  | 'midweek_bible_study'
  | 'youth_ministry'
  | 'small_groups'
  | 'personal_study'
  | 'sermon_series_planning';

interface OnboardingData {
  ministryRole: MinistryRole | null;
  denomination: Denomination | null;
  denominationOther: string;
  congregationSize: CongregationSize | null;
  useCases: UseCase[];
}

// ── Static data ────────────────────────────────────────────────────────────────

const ROLES: { value: MinistryRole; icon: React.ReactNode; description: string }[] = [
  { value: 'Senior / Lead Pastor',        icon: <Crown className="w-5 h-5" />,    description: 'Lead preaching & vision' },
  { value: 'Associate Pastor',            icon: <Users className="w-5 h-5" />,    description: 'Supporting pastoral role' },
  { value: 'Youth Pastor',               icon: <Megaphone className="w-5 h-5" />, description: 'Youth & student ministry' },
  { value: "Children's Ministry Leader", icon: <Heart className="w-5 h-5" />,    description: 'Kids & family ministry' },
  { value: 'Bible Study Leader',         icon: <BookOpen className="w-5 h-5" />,  description: 'Small group & Bible study' },
  { value: 'Worship Leader',             icon: <Music className="w-5 h-5" />,     description: 'Worship & creative arts' },
  { value: 'Chaplain',                   icon: <Building2 className="w-5 h-5" />, description: 'Hospital, military, or prison' },
  { value: 'Seminary Student',           icon: <Scroll className="w-5 h-5" />,    description: 'Academic / in training' },
  { value: 'Lay Preacher / Evangelist',  icon: <Megaphone className="w-5 h-5" />, description: 'Occasional preaching' },
  { value: 'Personal Study',             icon: <User className="w-5 h-5" />,      description: 'Personal devotion & study' },
];

const DENOMINATIONS: Denomination[] = [
  'Baptist',
  'Reformed / Presbyterian',
  'Methodist / Wesleyan',
  'Lutheran',
  'Anglican / Episcopal',
  'Pentecostal / Charismatic',
  'Non-denominational',
  'Catholic',
  'Other',
];

const CONGREGATION_SIZES: CongregationSize[] = [
  'Under 50', '50–150', '150–500', '500–1,000', '1,000+',
];

const USE_CASES: { value: UseCase; label: string; description: string }[] = [
  { value: 'sunday_sermons',         label: 'Sunday Sermons',         description: 'Weekly preaching preparation' },
  { value: 'midweek_bible_study',    label: 'Midweek Bible Study',    description: 'Wednesday services & studies' },
  { value: 'youth_ministry',         label: 'Youth Ministry',         description: 'Teen & student content' },
  { value: 'small_groups',           label: 'Small Groups',           description: 'Community group resources' },
  { value: 'personal_study',         label: 'Personal Study',         description: 'Devotional & personal growth' },
  { value: 'sermon_series_planning', label: 'Series Planning',        description: 'Multi-week sermon series' },
];

const TOTAL_STEPS = 3;

// ── Progress bar ───────────────────────────────────────────────────────────────

function ProgressBar({ step }: { step: number }) {
  const labels = ['Your Role', 'Your Tradition', 'Your Ministry'];
  return (
    <div className="flex items-center gap-2 mb-8">
      {labels.map((label, i) => {
        const idx = i + 1;
        const done = idx < step;
        const active = idx === step;
        return (
          <React.Fragment key={i}>
            <div className="flex flex-col items-center gap-1 min-w-0">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                done ? 'bg-electric text-white' : active ? 'bg-electric text-white ring-4 ring-electric/20' : 'bg-sand/40 text-slate/50'
              }`}>
                {done ? <Check className="w-3.5 h-3.5" /> : idx}
              </div>
              <span className={`text-[10px] font-medium whitespace-nowrap hidden sm:block ${active ? 'text-electric' : done ? 'text-slate/60' : 'text-slate/30'}`}>
                {label}
              </span>
            </div>
            {i < labels.length - 1 && (
              <div className={`flex-1 h-0.5 mt-[-10px] sm:mt-[-18px] rounded-full transition-all ${idx < step ? 'bg-electric' : 'bg-sand/40'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Step 1 — Role ──────────────────────────────────────────────────────────────

function StepRole({ value, onChange }: { value: MinistryRole | null; onChange: (v: MinistryRole) => void }) {
  return (
    <div>
      <h2 className="text-xl font-bold text-midnight mb-1">What's your ministry role?</h2>
      <p className="text-sm text-slate mb-5">This helps us tailor AI content to your specific context.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {ROLES.map(role => (
          <button
            key={role.value}
            type="button"
            onClick={() => onChange(role.value)}
            className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
              value === role.value
                ? 'border-electric bg-electric/5'
                : 'border-sand/30 hover:border-electric/40 hover:bg-sand/20'
            }`}
          >
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-colors ${
              value === role.value ? 'bg-electric/15 text-electric' : 'bg-sand/30 text-slate/60'
            }`}>
              {role.icon}
            </div>
            <div className="min-w-0">
              <div className={`text-sm font-semibold leading-snug ${value === role.value ? 'text-electric' : 'text-midnight'}`}>
                {role.value}
              </div>
              <div className="text-xs text-slate/60 truncate">{role.description}</div>
            </div>
            {value === role.value && (
              <Check className="w-4 h-4 text-electric shrink-0 ml-auto" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Step 2 — Denomination ──────────────────────────────────────────────────────

function StepDenomination({
  value, otherText, onChange, onOtherChange,
}: {
  value: Denomination | null;
  otherText: string;
  onChange: (v: Denomination) => void;
  onOtherChange: (v: string) => void;
}) {
  return (
    <div>
      <h2 className="text-xl font-bold text-midnight mb-1">Your church tradition</h2>
      <p className="text-sm text-slate mb-5">We'll shape theological framing and terminology to fit your tradition.</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {DENOMINATIONS.map(d => (
          <button
            key={d}
            type="button"
            onClick={() => onChange(d)}
            className={`px-4 py-2 rounded-full border-2 text-sm font-medium transition-all ${
              value === d
                ? 'border-electric bg-electric text-white'
                : 'border-sand/30 text-midnight hover:border-electric/50 hover:bg-sand/20'
            }`}
          >
            {d}
          </button>
        ))}
      </div>
      {value === 'Other' && (
        <div className="mt-3">
          <input
            type="text"
            placeholder="Tell us your tradition (optional)"
            value={otherText}
            onChange={e => onOtherChange(e.target.value)}
            className="w-full border border-sand/40 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-electric/40 focus:border-electric text-midnight placeholder-slate/40"
            autoFocus
          />
        </div>
      )}
    </div>
  );
}

// ── Step 3 — Ministry details ──────────────────────────────────────────────────

function StepMinistry({
  congregationSize, useCases, onSizeChange, onUseCaseToggle,
}: {
  congregationSize: CongregationSize | null;
  useCases: UseCase[];
  onSizeChange: (v: CongregationSize) => void;
  onUseCaseToggle: (v: UseCase) => void;
}) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-midnight mb-1">Your ministry context</h2>
        <p className="text-sm text-slate mb-5">A few final details to personalise your experience.</p>

        <label className="block text-xs font-semibold text-slate uppercase tracking-wider mb-2">Congregation Size</label>
        <div className="flex flex-wrap gap-2">
          {CONGREGATION_SIZES.map(size => (
            <button
              key={size}
              type="button"
              onClick={() => onSizeChange(size)}
              className={`px-4 py-2 rounded-full border-2 text-sm font-medium transition-all ${
                congregationSize === size
                  ? 'border-electric bg-electric text-white'
                  : 'border-sand/30 text-midnight hover:border-electric/50 hover:bg-sand/20'
              }`}
            >
              {size}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate uppercase tracking-wider mb-2">
          Primary Use Cases <span className="text-slate/40 normal-case font-normal">(select all that apply)</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {USE_CASES.map(uc => {
            const selected = useCases.includes(uc.value);
            return (
              <button
                key={uc.value}
                type="button"
                onClick={() => onUseCaseToggle(uc.value)}
                className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                  selected
                    ? 'border-electric bg-electric/5'
                    : 'border-sand/30 hover:border-electric/40 hover:bg-sand/20'
                }`}
              >
                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-all ${
                  selected ? 'border-electric bg-electric' : 'border-sand/50'
                }`}>
                  {selected && <Check className="w-2.5 h-2.5 text-white" />}
                </div>
                <div>
                  <div className={`text-sm font-semibold ${selected ? 'text-electric' : 'text-midnight'}`}>{uc.label}</div>
                  <div className="text-xs text-slate/60">{uc.description}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export function AccountSetup() {
  const navigate = useNavigate();
  const [step, setStep]   = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prefetched, setPrefetched] = useState(false);

  const [data, setData] = useState<OnboardingData>({
    ministryRole: null,
    denomination: null,
    denominationOther: '',
    congregationSize: null,
    useCases: [],
  });

  // Pre-populate from existing profile (idempotent revisits)
  useEffect(() => {
    supabase.auth.getUser().then(async ({ data: { user } }) => {
      if (!user) return;
      const { data: profile } = await supabase
        .from('profiles')
        .select('ministry_role, denomination, congregation_size, primary_use_cases')
        .eq('id', user.id)
        .single();
      if (!profile) { setPrefetched(true); return; }

      const denom = profile.denomination as Denomination | null;
      const isOtherDenom = denom && !DENOMINATIONS.filter(d => d !== 'Other').includes(denom);

      setData(prev => ({
        ...prev,
        ministryRole: (profile.ministry_role as MinistryRole) ?? null,
        denomination: isOtherDenom ? 'Other' : denom,
        denominationOther: isOtherDenom ? (denom?.replace(/^Other:\s*/i, '') ?? '') : '',
        congregationSize: (profile.congregation_size as CongregationSize) ?? null,
        useCases: (profile.primary_use_cases as UseCase[]) ?? [],
      }));
      setPrefetched(true);
    });
  }, []);

  const handleSubmit = async (skipping = false) => {
    setLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const denominationValue =
        data.denomination === 'Other'
          ? data.denominationOther.trim()
            ? `Other: ${data.denominationOther.trim()}`
            : 'Other'
          : data.denomination;

      await supabase.from('profiles').upsert({
        id: user.id,
        ministry_role: data.ministryRole,
        denomination: denominationValue,
        congregation_size: data.congregationSize,
        primary_use_cases: data.useCases.length > 0 ? data.useCases : null,
        ...(skipping
          ? { onboarding_skipped_at: new Date().toISOString() }
          : { onboarding_completed_at: new Date().toISOString(), onboarding_skipped_at: null }),
      }, { onConflict: 'id' });

      navigate('/dashboard', { replace: true });
    } catch (err) {
      logger.error('AccountSetup submit error:', err);
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const canAdvance = () => {
    if (step === 1) return !!data.ministryRole;
    if (step === 2) return !!data.denomination;
    return true; // step 3 — congregation size + use cases are optional
  };

  const handleNext = () => {
    if (step < TOTAL_STEPS) setStep(s => s + 1);
    else handleSubmit(false);
  };

  const toggleUseCase = (uc: UseCase) => {
    setData(prev => ({
      ...prev,
      useCases: prev.useCases.includes(uc)
        ? prev.useCases.filter(u => u !== uc)
        : [...prev.useCases, uc],
    }));
  };

  if (!prefetched) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sand/30 to-blue-50/30 flex items-center justify-center">
        <Loader2 className="h-7 w-7 animate-spin text-electric" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sand/30 to-blue-50/30 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl border border-sand/20 w-full max-w-xl p-8">

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-electric/10 flex items-center justify-center shrink-0">
            <BookOpen className="h-5 w-5 text-electric" />
          </div>
          <div>
            <h1 className="text-base font-bold text-midnight leading-tight">Set up your account</h1>
            <p className="text-xs text-slate/60">Helps us personalise AI content to your ministry</p>
          </div>
          <button
            type="button"
            onClick={() => handleSubmit(true)}
            disabled={loading}
            className="ml-auto text-xs text-slate/40 hover:text-slate transition-colors shrink-0"
          >
            Skip for now
          </button>
        </div>

        <ProgressBar step={step} />

        {/* Step content */}
        <div className="min-h-[320px]">
          {step === 1 && (
            <StepRole
              value={data.ministryRole}
              onChange={v => setData(prev => ({ ...prev, ministryRole: v }))}
            />
          )}
          {step === 2 && (
            <StepDenomination
              value={data.denomination}
              otherText={data.denominationOther}
              onChange={v => setData(prev => ({ ...prev, denomination: v }))}
              onOtherChange={v => setData(prev => ({ ...prev, denominationOther: v }))}
            />
          )}
          {step === 3 && (
            <StepMinistry
              congregationSize={data.congregationSize}
              useCases={data.useCases}
              onSizeChange={v => setData(prev => ({ ...prev, congregationSize: v }))}
              onUseCaseToggle={toggleUseCase}
            />
          )}
        </div>

        {error && (
          <p className="mt-4 text-sm text-crimson bg-crimson/5 border border-crimson/20 rounded-lg px-3 py-2">
            {error}
          </p>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-sand/20">
          <button
            type="button"
            onClick={() => setStep(s => s - 1)}
            disabled={step === 1 || loading}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-slate hover:text-midnight disabled:opacity-0 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>

          <div className="flex items-center gap-3">
            <span className="text-xs text-slate/40">{step} of {TOTAL_STEPS}</span>
            <button
              type="button"
              onClick={handleNext}
              disabled={loading || !canAdvance()}
              className="flex items-center gap-2 px-5 py-2.5 bg-electric text-white rounded-xl text-sm font-semibold hover:bg-electric/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm shadow-electric/20"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : step === TOTAL_STEPS ? (
                <>Finish <Check className="w-4 h-4" /></>
              ) : (
                <>Next <ChevronRight className="w-4 h-4" /></>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
